import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, Home, FileText } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
const money = (n) => `$${Number(n || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DocLink = ({ token, kind, id, label, testId }) => (
  <a data-testid={testId} href={`${process.env.REACT_APP_BACKEND_URL}/api/public/quote-acceptance/${token}/pdf/${kind}/${id}`} target="_blank" rel="noreferrer"
    className="inline-flex items-center gap-1 text-xs text-sky-700 hover:text-sky-900 underline decoration-dotted"><FileText size={12} />{label}</a>
);

export default function QuoteAcceptancePublicPage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState([]);
  const [terms, setTerms] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/public/quote-acceptance/${token}`).then(({ data }) => {
      setD(data);
      if (data.acceptance?.quote_ids) setPicked(data.acceptance.quote_ids);
    }).catch((e) => setError(e?.response?.data?.detail || 'This link is not valid.'));
  }, [token]);

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const submit = async () => {
    setBusy(true); setError('');
    try { await api.post(`/public/quote-acceptance/${token}`, { quote_ids: picked, accept_terms: terms, note }); setDone(true); }
    catch (e) { setError(e?.response?.data?.detail || 'Could not send — please try again.'); }
    finally { setBusy(false); }
  };

  const showSimple = d && ['simple', 'both'].includes(d.terms_mode);
  const showFull = d && ['full', 'both'].includes(d.terms_mode);
  const confirmed = d?.acceptance?.status === 'confirmed';

  return (
    <div className="min-h-screen bg-[#F7F6F3] py-10 px-4">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-2 mb-6 text-[#D35400]"><Home size={20} /><span className="font-bold tracking-tight text-slate-900">Roofs To Remedy</span></div>
        {error && !d ? <p data-testid="qaccept-error">{error}</p> : !d ? <Loader2 className="animate-spin text-slate-400" /> : (done || confirmed) ? (
          <div data-testid="qaccept-done" className="bg-white rounded-lg border border-[#E2E2DF] p-8 text-center">
            <CheckCircle2 className="mx-auto text-emerald-600 mb-3" size={36} />
            <p className="text-slate-700">{confirmed ? 'Our office has already confirmed your acceptance — thank you.' : "Thank you — we've received your choice. We'll be in touch to confirm, and your booking is secured once any deposit is paid."}</p>
          </div>
        ) : (
          <div data-testid="qaccept-page" className="bg-white rounded-lg border border-[#E2E2DF] p-6 sm:p-8 space-y-5">
            <h1 className="text-2xl font-bold text-slate-900">Hi {d.first_name || 'there'}, which quote would you like?</h1>
            <p className="text-sm text-slate-600">{d.address}</p>
            {d.acceptance?.submitted_at && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="qaccept-previous">You already sent a choice — you can change it below until our office confirms it.</p>}
            <div className="space-y-2">
              {d.quotes.map((q) => (
                <label key={q.id} data-testid={`qaccept-quote-${q.id}`} className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-colors ${picked.includes(q.id) ? 'border-[#D35400] bg-orange-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                  <input type="checkbox" className="mt-0.5 w-5 h-5 accent-[#D35400]" checked={picked.includes(q.id)} onChange={() => toggle(q.id)} data-testid={`qaccept-quote-check-${q.id}`} />
                  <span className="flex-1">
                    <span className="flex justify-between gap-2 text-sm font-semibold text-slate-900"><span>{q.label}{q.variant_label ? ` · ${q.variant_label}` : ''}</span><span className="font-mono text-right">{money(q.total_inc_gst)}<span className="block text-[10px] font-sans font-normal text-slate-500">incl. GST</span></span></span>
                    <span className="flex flex-wrap gap-3 mt-1">
                      <DocLink token={token} kind="quotation" id={q.id} label={`Quotation${q.appraisal_no ? ` A${q.appraisal_no}` : ''}`} testId={`qaccept-doc-quote-${q.id}`} />
                      {showSimple && <DocLink token={token} kind="terms_simple" id={q.id} label="Terms of Trade" testId={`qaccept-doc-terms-${q.id}`} />}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            {showFull && <DocLink token={token} kind="terms_full" id={d.quotes[0]?.id} label="Terms of Trade (full)" testId="qaccept-doc-terms-full" />}
            <label className="flex items-start gap-2 text-sm text-slate-800" data-testid="qaccept-terms-label">
              <input type="checkbox" className="mt-0.5 w-5 h-5 accent-[#D35400]" checked={terms} onChange={(e) => setTerms(e.target.checked)} data-testid="qaccept-terms-check" />
              I have read and accept the Terms of Trade for the quote(s) I've picked.
            </label>
            <textarea data-testid="qaccept-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything we should know? (optional)" className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md" />
            <p className="text-xs text-slate-500">This is a pre-confirmation. Our office will confirm with you, and your booking is secured once any deposit is paid.</p>
            {error && <p className="text-sm text-red-600" data-testid="qaccept-submit-error">{error}</p>}
            <button data-testid="qaccept-submit" disabled={busy || !picked.length || !terms} onClick={submit} className="w-full py-2.5 rounded-md bg-[#D35400] hover:bg-[#A04000] text-white text-sm font-semibold disabled:opacity-40">
              {busy ? 'Sending…' : 'Send my choice'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
